import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Agent, AgentInfo } from "../../schema/types/agent/entity";
import { useGetAgentQuery } from "../../store/api/agentApi";
import { agentToInput, agentToPatch, inputToAgent } from "./utils";
import { getValidator } from "./create/validator";
import { AgentInput } from "../../schema/types/agent/input";
import { AgentDetailContent } from "./detailContent";
import { getDirtyChecker } from "./create/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  agent: Agent;
  agentInfo: AgentInfo[] | undefined;
}
export const AgentDetailForm: React.FC<Props> = ({ agent, agentInfo }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetAgentQuery(linksOf(agent).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = agentToInput(agent);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: agentToInput(agent) }));
  }, [agent.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: AgentInput) {
    try {
      let body = agentToPatch(inputToAgent(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(agent).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Agent is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content:
            "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <AgentDetailContent agent={agent} agentInfo={agentInfo} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(agent).self!} inputToItemFn={inputToAgent} />
      <CpPreviewModal inputToItemFn={inputToAgent} kind="agent" namePath="name" title="Agent" />
      <CpDebugModal inputToItemFn={inputToAgent} kind="agent" namePath="name" title="Agent" />
    </CpFormContext.Provider>
  );
};
